import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import styled from 'styled-components';
import { workspaceTitle } from '../../styles/pageStyles';
import { SharedDateTimePicker } from '../../components/common/DateTimePicker';
import useCurrentUserContext from 'contexts/currentUser';
import { getTimezoneDisplayName } from 'utils/timezone';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { parseISO } from 'date-fns';
import { logError } from 'components/utils/errors';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const GET_CLONED_STORY_DATA = gql`
  query GetClonedStoryData($storyId: ID!) {
    story(id: $storyId) {
      id
      title
      description
      storyFormat
      wordCount
      imageCount
      callToActionName
      callToActionDescription
      recommendedLinks
      publishAt
      contentPillarId
      audienceId
      brandProfileId
      campaigns {
        id
        name
      }
      brandProfile {
        id
        name
        campaigns {
          id
          name
        }
        pillars {
          id
          name
        }
        audiences {
          id
          name
        }
      }
    }
  }
`;

const UPDATE_CLONED_STORY = gql`
  mutation UpdateClonedStoryRequirements(
    $storyId: ID!
    $title: String
    $description: String
    $publishAt: ISO8601DateTime
    $wordCount: String
    $imageCount: Int
    $callToActionName: String
    $callToActionDescription: String
    $recommendedLinks: [String!]
    $contentPillarId: ID
    $audienceId: ID
    $campaignIds: [ID!]
  ) {
    updateStoryRequirements(
      input: {
        storyId: $storyId
        title: $title
        description: $description
        publishAt: $publishAt
        wordCount: $wordCount
        imageCount: $imageCount
        callToActionName: $callToActionName
        callToActionDescription: $callToActionDescription
        recommendedLinks: $recommendedLinks
        contentPillarId: $contentPillarId
        audienceId: $audienceId
        campaignIds: $campaignIds
      }
    ) {
      success
      errors {
        message
      }
      story {
        id
        title
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Constants
// These constants match the Rails StoryText::WORD_COUNT_OPTIONS, Story CTA
// options, and StoryFormat definitions. Ideally fetched from server to stay
// in sync, but hardcoded here for now.
// ---------------------------------------------------------------------------

// Canonical V2/V3-supported story formats — must match the BE enum at
// contently-contently `app/models/story_format.rb` `TEV2_SUPPORTED_FORMATS`
// (15 entries) and the labels at `config/locales/en.yml` under
// `helpers.label.story_format.*`. Three FE-only values that did not exist in
// the BE enum (`social_media`, `press_release`, `newsletter`) have been
// removed. Seven BE-valid formats that were missing have been added (sheet
// row 78). Kept in sync with `NewStoryPage.tsx STORY_FORMATS` constant.
const STORY_FORMATS: Record<string, string> = {
  article: 'Article / blog post',
  case_study: 'Case study',
  design: 'Design',
  ebook: 'eBook',
  email: 'Email',
  facebook: 'Facebook post',
  linked_in: 'LinkedIn post',
  original_research: 'Original research',
  photo: 'Photo',
  podcast: 'Podcast',
  presentation: 'Presentation / brochure',
  twitter: 'Tweet',
  video: 'Video',
  whitepaper: 'Whitepaper',
  other: 'Other',
};

const WORD_COUNT_OPTIONS = ['500', '800', '1000', '1200', '1500', '2000', '2500'];

const CALL_TO_ACTION_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'read_more_content', label: 'Read more content' },
  { value: 'subscribe', label: 'Subscribe' },
  { value: 'download', label: 'Download' },
  { value: 'sign_up', label: 'Sign up' },
  { value: 'learn_more', label: 'Learn more' },
  { value: 'contact_us', label: 'Contact us' },
];

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Container = styled.div`
  padding: 40px 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  ${workspaceTitle}
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #666;

  &.required::after {
    content: ' *';
    color: #d32f2f;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 2px rgba(0, 166, 194, 0.1);
  }
`;

const CloneBanner = styled.div`
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 24px;
  color: #856404;
`;

const ReadOnlyField = styled.div`
  padding: 10px 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #666;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #eee;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: 1px solid
    ${({ $variant = 'secondary' }) => ($variant === 'primary' ? '#0094ab' : '#ddd')};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $variant = 'secondary' }) =>
    $variant === 'primary' ? '#00a6c2' : 'transparent'};
  color: ${({ $variant = 'secondary' }) => ($variant === 'primary' ? 'white' : '#666')};

  &:hover {
    background: ${({ $variant = 'secondary' }) =>
      $variant === 'primary' ? '#0094ab' : '#f8f9fa'};
    border-color: ${({ $variant = 'secondary' }) =>
      $variant === 'primary' ? '#008299' : '#ccc'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddLinkButton = styled.button`
  background: none;
  border: none;
  color: #00a6c2;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const RemoveLinkButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    color: #dc3545;
  }
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const ErrorContainer = styled.div`
  padding: 40px 24px;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: #dc3545;
`;

const TimezoneHint = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const CampaignCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  text-transform: none;
  font-weight: normal;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface FormState {
  title: string;
  description: string;
  publishDateTime: Date | null;
  wordCount: string;
  imageCount: string;
  callToActionName: string;
  callToActionDescription: string;
  recommendedLinks: string[];
  contentPillarId: string;
  audienceId: string;
  campaignIds: string[];
}

export default function EditClonedBriefPage() {
  const { story_id: storyId } = useParams<{ story_id: string }>();
  const currentUser = useCurrentUserContext();

  const { data, loading, error } = useQuery(GET_CLONED_STORY_DATA, {
    variables: { storyId },
    skip: !storyId,
    fetchPolicy: 'network-only',
  });

  const [updateStory, { loading: saving }] = useMutation(UPDATE_CLONED_STORY);

  const [formData, setFormData] = useState<FormState>({
    title: '',
    description: '',
    publishDateTime: null,
    wordCount: '',
    imageCount: '',
    callToActionName: '',
    callToActionDescription: '',
    recommendedLinks: [''],
    contentPillarId: '',
    audienceId: '',
    campaignIds: [],
  });

  // Populate form when data arrives
  useEffect(() => {
    if (!data?.story) return;

    const s = data.story;
    setFormData({
      title: s.title || '',
      description: s.description || '',
      publishDateTime: s.publishAt ? parseISO(s.publishAt) : null,
      wordCount: s.wordCount?.toString() || '',
      imageCount: s.imageCount?.toString() || '',
      callToActionName: s.callToActionName || '',
      callToActionDescription: s.callToActionDescription || '',
      recommendedLinks:
        s.recommendedLinks && s.recommendedLinks.length > 0 ? s.recommendedLinks : [''],
      contentPillarId: s.contentPillarId || '',
      audienceId: s.audienceId || '',
      campaignIds: s.campaigns?.map((c: { id: string }) => c.id) || [],
    });
  }, [data]);

  // Helpers
  const story = data?.story;
  const brandProfile = story?.brandProfile;

  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Recommended links management
  const addLink = () => {
    setFormData((prev) => ({ ...prev, recommendedLinks: [...prev.recommendedLinks, ''] }));
  };

  const removeLink = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      recommendedLinks: prev.recommendedLinks.filter((_, i) => i !== index),
    }));
  };

  const updateLink = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      recommendedLinks: prev.recommendedLinks.map((link, i) => (i === index ? value : link)),
    }));
  };

  // Campaign toggle
  const toggleCampaign = (campaignId: string) => {
    setFormData((prev) => ({
      ...prev,
      campaignIds: prev.campaignIds.includes(campaignId)
        ? prev.campaignIds.filter((id) => id !== campaignId)
        : [...prev.campaignIds, campaignId],
    }));
  };

  // Save handler
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async (redirectTo: 'preview' | 'workflow') => {
    if (!storyId || isSaving) return;
    setIsSaving(true);

    if (!formData.title.trim()) {
      toast.error('Title is required.');
      setIsSaving(false);
      return;
    }

    // Filter out empty links
    const cleanedLinks = formData.recommendedLinks.filter((link) => link.trim() !== '');

    try {
      const result = await updateStory({
        variables: {
          storyId,
          title: formData.title,
          description: formData.description,
          publishAt: formData.publishDateTime ? formData.publishDateTime.toISOString() : null,
          wordCount: formData.wordCount || null,
          imageCount: formData.imageCount ? parseInt(formData.imageCount, 10) : null,
          callToActionName: formData.callToActionName || null,
          callToActionDescription: formData.callToActionDescription || null,
          recommendedLinks: cleanedLinks.length > 0 ? cleanedLinks : null,
          contentPillarId: formData.contentPillarId || null,
          audienceId: formData.audienceId || null,
          campaignIds: formData.campaignIds.length > 0 ? formData.campaignIds : [],
        },
      });

      const mutationResult = result.data?.updateStoryRequirements;

      if (mutationResult?.success) {
        toast.success('Story brief saved successfully.');

        if (redirectTo === 'preview') {
          window.location.href = `/stories/${storyId}/edits?preview=true`;
        } else {
          window.location.href = `/stories/${storyId}/workflow/edit`;
        }
      } else {
        const errorMessages =
          mutationResult?.errors?.map((e: { message: string }) => e.message).join(', ') ||
          'Unknown error';
        toast.error(`Failed to save: ${errorMessages}`);
      }
    } catch (err) {
      console.error('Failed to save cloned story brief:', err);
      logError(err instanceof Error ? err : new Error(`Clone brief save error: ${err}`));
      toast.error('An error occurred while saving the story brief. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!storyId) {
    return (
      <Container>
        <CloneBanner style={{ background: '#f8d7da', borderColor: '#f5c6cb', color: '#721c24' }}>
          No story ID found in the URL. Please go back and try cloning again.
        </CloneBanner>
      </Container>
    );
  }

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <h2>Error loading story</h2>
        <p>{error.message}</p>
      </ErrorContainer>
    );
  }

  if (!story) {
    return (
      <ErrorContainer>
        <h2>Story not found</h2>
      </ErrorContainer>
    );
  }

  const timezoneDisplay = getTimezoneDisplayName(currentUser);

  return (
    <Container>
      <CloneBanner>
        You are editing a cloned story. Review the details below before proceeding.
      </CloneBanner>

      <PageTitle>Create Story Brief</PageTitle>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {/* Publication (read-only) */}
        <Section>
          <SectionTitle>Publication</SectionTitle>
          <FormGroup>
            <Label>Publication</Label>
            <ReadOnlyField>{brandProfile?.name || 'N/A'}</ReadOnlyField>
          </FormGroup>
        </Section>

        {/* Story Details */}
        <Section>
          <SectionTitle>Story Details</SectionTitle>

          <FormGroup>
            <Label className="required">Title</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Enter story title"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <TextArea
              rows={4}
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Enter a description or brief for this story"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Story Format</Label>
              <ReadOnlyField>
                {STORY_FORMATS[story.storyFormat] || story.storyFormat || 'N/A'}
              </ReadOnlyField>
            </FormGroup>

            <FormGroup>
              <Label>Word Count</Label>
              <Select
                value={formData.wordCount}
                onChange={(e) => updateField('wordCount', e.target.value)}
              >
                <option value="">Select word count</option>
                {WORD_COUNT_OPTIONS.map((wc) => (
                  <option key={wc} value={wc}>
                    {wc} words
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Image Count</Label>
              <Input
                type="number"
                min={0}
                value={formData.imageCount}
                onChange={(e) => updateField('imageCount', e.target.value)}
                placeholder="Number of images"
              />
            </FormGroup>

            <FormGroup>
              <Label>Publish Date</Label>
              <SharedDateTimePicker
                selected={formData.publishDateTime}
                onChange={(date) => updateField('publishDateTime', date)}
              />
              <TimezoneHint>All times in {timezoneDisplay}</TimezoneHint>
            </FormGroup>
          </FormRow>
        </Section>

        {/* Call to Action */}
        <Section>
          <SectionTitle>Call to Action</SectionTitle>

          <FormGroup>
            <Label>Call to Action</Label>
            <Select
              value={formData.callToActionName}
              onChange={(e) => updateField('callToActionName', e.target.value)}
            >
              {CALL_TO_ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormGroup>

          {formData.callToActionName && (
            <FormGroup>
              <Label>Call to Action Description</Label>
              <TextArea
                rows={3}
                value={formData.callToActionDescription}
                onChange={(e) => updateField('callToActionDescription', e.target.value)}
                placeholder="Describe what the reader should do"
              />
            </FormGroup>
          )}
        </Section>

        {/* Content Strategy */}
        <Section>
          <SectionTitle>Content Strategy</SectionTitle>

          <FormRow>
            <FormGroup>
              <Label>Content Pillar</Label>
              <Select
                value={formData.contentPillarId}
                onChange={(e) => updateField('contentPillarId', e.target.value)}
              >
                <option value="">Select a pillar</option>
                {brandProfile?.pillars?.map((p: { id: string; name: string }) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Audience</Label>
              <Select
                value={formData.audienceId}
                onChange={(e) => updateField('audienceId', e.target.value)}
              >
                <option value="">Select an audience</option>
                {brandProfile?.audiences?.map((a: { id: string; name: string }) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          {brandProfile?.campaigns && brandProfile.campaigns.length > 0 && (
            <FormGroup>
              <Label>Campaigns</Label>
              <CampaignCheckboxList>
                {brandProfile.campaigns.map((c: { id: string; name: string }) => (
                  <CheckboxLabel key={c.id}>
                    <input
                      type="checkbox"
                      checked={formData.campaignIds.includes(c.id)}
                      onChange={() => toggleCampaign(c.id)}
                    />
                    {c.name}
                  </CheckboxLabel>
                ))}
              </CampaignCheckboxList>
            </FormGroup>
          )}
        </Section>

        {/* Recommended Links */}
        <Section>
          <SectionTitle>Recommended Links</SectionTitle>

          {formData.recommendedLinks.map((link, index) => (
            <LinkRow key={index}>
              <Input
                type="url"
                value={link}
                onChange={(e) => updateLink(index, e.target.value)}
                placeholder="https://example.com"
                style={{ flex: 1 }}
              />
              {formData.recommendedLinks.length > 1 && (
                <RemoveLinkButton type="button" onClick={() => removeLink(index)} title="Remove">
                  &times;
                </RemoveLinkButton>
              )}
            </LinkRow>
          ))}

          <AddLinkButton type="button" onClick={addLink}>
            + Add another link
          </AddLinkButton>
        </Section>

        {/* Actions */}
        <ButtonRow>
          <Button
            type="button"
            $variant="secondary"
            disabled={saving}
            onClick={() => handleSave('preview')}
          >
            {saving ? 'Saving...' : 'Save and exit'}
          </Button>
          <Button
            type="button"
            $variant="primary"
            disabled={saving}
            onClick={() => handleSave('workflow')}
          >
            {saving ? 'Saving...' : 'Save and add workflow'}
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
}
